import { fmtNum } from '@slims/shared/calculations';
import type { DashboardKpiSummary } from '@slims/shared/types';
import type { ForecastDay } from '@/hooks/use-forecast-data';

/**
 * Avvisi di sistema derivati dai dati (INB — «notifiche inline»).
 *
 * Erano banner in cima a dashboard, MySlims, Previsione AI e Report builder:
 * strisce che ripetevano lo stato della configurazione su ogni pagina. Il
 * contenuto è lo stesso, ma vive nel pannello «Notifiche di Sistema» del centro
 * notifiche, dove l'utente si aspetta di trovarlo.
 *
 * Sono **calcolati**, non righe di `notifications`: nascono e spariscono con i
 * dati che descrivono, quindi non hanno stato letto/non letto né cancellazione.
 * Per questo il modulo è una funzione pura — nessuna fetch, nessuna scrittura.
 */

export type SystemAlertAudience = 'admin' | 'member';

export interface SystemAlert {
	/** Stabile fra un ricalcolo e l'altro: identifica l'avviso, non l'istanza. */
	id: string;
	emoji: string;
	title: string;
	message: string;
	/** Pillola di stato a destra (LIVE, ND, Dati Reali, …). */
	badge: string;
	/**
	 * `true` quando l'avviso segnala qualcosa di incompleto (misuratori non
	 * integrati): la riga viene evidenziata come le scadenze.
	 */
	actionRequired: boolean;
	/** Pagina di cui l'avviso parla: il click ci porta. */
	href: string;
	audience: SystemAlertAudience[];
}

export interface SystemAlertInput {
	/** KPI del mese corrente; `null` finché la dashboard non ha risposto. */
	month: DashboardKpiSummary['month'] | null;
	/** Totali storici della configurazione. */
	totals: { totalImmessa: number; totalPrelevata: number; totalCondivisa: number };
	/** Proiezione 7 giorni. */
	forecast: ForecastDay[];
	/** Giorno di picco scelto dal modello fisico, se ci sono POD di immissione. */
	forecastPeakDate: string | null;
	forecastSource: 'real-data' | 'config-estimate' | 'no-data';
	/** Copertura nuvolosa corrente in %, `null` senza dati meteo. */
	cloudCoverPct: number | null;
}

/** kg di CO₂ evitati per kWh condiviso — stesso fattore usato in MySlims. */
const CO2_KG_PER_KWH = 0.233;

const DAYS_IT = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

function dayLabel(day: ForecastDay, index: number): string {
	if (!day.date) return DAYS_IT[index] ?? `G${index + 1}`;
	const parsed = new Date(`${day.date}T12:00:00`);
	if (Number.isNaN(parsed.getTime())) return DAYS_IT[index] ?? `G${index + 1}`;
	return parsed.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric' }).replace('.', '');
}

/**
 * Giorno di massima produzione. Con i POD di immissione censiti lo decide il
 * modello fisico (`forecastPeakDate`); senza, resta il massimo della proiezione
 * stagionale — la stessa scelta della vista Previsione AI.
 */
function peakDayIndex(forecast: ForecastDay[], peakDate: string | null): number {
	if (peakDate) {
		const fromModel = forecast.findIndex((d) => d.date === peakDate);
		if (fromModel >= 0) return fromModel;
	}
	let best = 0;
	for (let i = 1; i < forecast.length; i += 1) {
		if ((forecast[i]?.production ?? 0) > (forecast[best]?.production ?? 0)) best = i;
	}
	return best;
}

function cloudImpactText(cloudCoverPct: number | null): string | null {
	if (cloudCoverPct === null) return null;
	if (cloudCoverPct > 50) {
		return `Copertura nuvolosa ${cloudCoverPct}% - produzione ridotta del ${Math.round(cloudCoverPct * 0.55)}%`;
	}
	if (cloudCoverPct <= 20) return `Cielo sereno (${cloudCoverPct}% nuvole) - condizioni ottimali`;
	return `Copertura parziale ${cloudCoverPct}% - produzione nella norma`;
}

function aiPredictionAlert(input: SystemAlertInput): SystemAlert {
	const { month } = input;
	const productionKwh = month?.productionKwh ?? 0;
	const consumptionKwh = month?.consumptionKwh ?? 0;
	const sharedKwh = month?.sharedKwh ?? 0;
	const hasMonthData =
		month !== null && (productionKwh > 0 || consumptionKwh > 0 || sharedKwh > 0);

	if (!hasMonthData) {
		return {
			id: 'sys-ai-prediction',
			emoji: '🤖',
			title: 'AI Prediction Engine - In attesa dati GSE',
			message:
				'Integra i misuratori GSE (portale SPC) per visualizzare energia condivisa e autoconsumo in tempo reale.',
			badge: 'ND',
			actionRequired: true,
			href: '/dashboard',
			audience: ['admin'],
		};
	}

	// Il periodo va detto (INB-371): sono i numeri della Dashboard dal 1° del
	// mese a oggi, mentre la pagina Energia condivisa guarda il mese intero, giorni
	// previsti compresi — senza, i due numeri sembravano in contraddizione.
	const parts = [
		`Energia condivisa ${fmtNum(sharedKwh)} kWh`,
		`autoconsumo collettivo ${month.selfConsumptionPct !== null ? `${month.selfConsumptionPct}%` : '-'}`,
	];

	return {
		id: 'sys-ai-prediction',
		emoji: '⚡',
		title: 'Mese in corso, dal 1° a oggi',
		message: `${parts.join(' · ')}. La pagina Energia condivisa guarda il mese intero, giorni previsti compresi.`,
		badge: 'Stima',
		actionRequired: false,
		href: '/dashboard',
		audience: ['admin'],
	};
}

function memberContributionAlert(input: SystemAlertInput): SystemAlert {
	const { totals } = input;
	const hasRealData = totals.totalCondivisa > 0;

	if (!hasRealData) {
		return {
			id: 'sys-member-contribution',
			emoji: '👋',
			title: 'Benvenuto nella Comunità Energetica',
			message:
				'Collega i misuratori GSE (portale SPC) per visualizzare il tuo contributo energetico in tempo reale.',
			badge: 'ND',
			actionRequired: true,
			href: '/myslims',
			audience: ['member'],
		};
	}

	const autoconsumo =
		totals.totalImmessa > 0
			? Math.min(100, Math.round((totals.totalCondivisa / totals.totalImmessa) * 100))
			: null;
	const sharedKwhMensile = Math.round(totals.totalCondivisa / 12);
	const co2T = (totals.totalCondivisa * CO2_KG_PER_KWH) / 1000;

	const parts = [`Autoconsumo collettivo ${autoconsumo !== null ? `${autoconsumo}%` : '-'}`];
	if (sharedKwhMensile > 0)
		parts.push(`energia condivisa in media ${fmtNum(sharedKwhMensile)} kWh/mese`);
	if (co2T > 0) parts.push(`CO₂ evitata ${(Math.round(co2T * 10) / 10).toFixed(1)} t`);

	// Numeri della configurazione, non del socio: il titolo non parla del «tuo
	// impianto» (che il socio consumatore non ha) né di «questo momento» (INB-371).
	return {
		id: 'sys-member-contribution',
		emoji: '⚡',
		title: 'La tua configurazione, da quando è attiva',
		message: parts.join(' · '),
		badge: 'Riepilogo',
		actionRequired: false,
		href: '/myslims',
		audience: ['member'],
	};
}

function forecastAlert(input: SystemAlertInput): SystemAlert {
	const { forecast, forecastPeakDate, forecastSource, cloudCoverPct } = input;
	const badge =
		// È sempre una previsione: «Dati Reali» diceva della fonte, ma si leggeva
		// come se il numero fosse misurato (N-16). E la fonte è la curva servita,
		// che su una configurazione nuova è tutta stimata: «dati reali» non lo è.
		forecastSource === 'real-data'
			? 'Previsione dallo storico'
			: forecastSource === 'config-estimate'
				? 'Stima'
				: 'ND';
	const index = peakDayIndex(forecast, forecastPeakDate);
	const peak = forecast[index] ?? null;

	if (!peak || peak.production <= 0) {
		return {
			id: 'sys-ai-forecast',
			emoji: '🔮',
			title: 'Analisi predittiva - Settimana corrente',
			message: 'Integra i misuratori GSE per attivare la previsione basata su dati reali.',
			badge,
			actionRequired: true,
			href: '/forecast',
			audience: ['admin'],
		};
	}

	const cloudImpact = cloudImpactText(cloudCoverPct);
	return {
		id: 'sys-ai-forecast',
		emoji: '🔮',
		title: 'Analisi predittiva - Settimana corrente',
		message: `Giorno di picco: ${dayLabel(peak, index)} → ~${fmtNum(peak.production)} kWh (conf. ${peak.confidence}%).${cloudImpact ? ` ${cloudImpact}.` : ''}`,
		badge,
		actionRequired: false,
		href: '/forecast',
		audience: ['admin'],
	};
}

/**
 * Il Report builder non dipende dai dati: l'avviso resta la sua descrizione,
 * che prima stava nell'intestazione della pagina.
 */
const REPORT_BUILDER_ALERT: SystemAlert = {
	id: 'sys-report-builder',
	emoji: '📄',
	title: 'Report builder disponibile',
	message:
		'Componi report su misura scegliendo destinatario, periodo e blocchi di contenuto sull’energia della configurazione. Anteprima in tempo reale, export PDF ed Excel.',
	badge: 'Strumento',
	actionRequired: false,
	href: '/reports',
	audience: ['admin'],
};

/** Avvisi visibili al ruolo indicato, nell'ordine in cui vanno mostrati. */
export function buildSystemAlerts(
	input: SystemAlertInput,
	audience: SystemAlertAudience,
): SystemAlert[] {
	const all = [
		aiPredictionAlert(input),
		memberContributionAlert(input),
		forecastAlert(input),
		REPORT_BUILDER_ALERT,
	];
	return all
		.filter((a) => a.audience.includes(audience))
		.sort((a, b) => Number(b.actionRequired) - Number(a.actionRequired));
}
